/**
 * FiGroupSplitChart — donut of intra-group vs external revenue, with a
 * legend that also surfaces each side's margin %.
 */
import { useMemo } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { GroupSplit } from './fiTypes'
import { formatCurrency, formatPct } from './fiFormat'

interface FiGroupSplitChartProps {
  groupSplit: GroupSplit
}

const INTRA_COLOR = '#14b8a6'
const EXTERNAL_COLOR = '#64748b'

interface TooltipPayloadEntry {
  name?: string
  value?: number
  payload?: { name: string; value: number }
}

function SplitTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayloadEntry[] }) {
  if (!active || !payload || payload.length === 0) return null
  const p = payload[0]
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-2.5 text-sm">
      <span className="text-white font-medium">{p.payload?.name}</span>
      <span className="text-gray-300 ml-2">{formatCurrency(p.payload?.value ?? 0)}</span>
    </div>
  )
}

export default function FiGroupSplitChart({ groupSplit }: FiGroupSplitChartProps) {
  const { intragroupRevenue, externalRevenue, intragroupMarginPct, externalMarginPct } = groupSplit
  const total = intragroupRevenue + externalRevenue

  const data = useMemo(() => [
    { name: 'Intra-grupo', value: intragroupRevenue, color: INTRA_COLOR },
    { name: 'Externo', value: externalRevenue, color: EXTERNAL_COLOR },
  ], [intragroupRevenue, externalRevenue])

  const hasData = total > 0

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Intra-grupo vs Externo</h3>
        <p className="text-gray-400 text-sm">Revenue del período</p>
      </div>

      {!hasData ? (
        <div className="text-center py-8 text-gray-400">Sin datos suficientes.</div>
      ) : (
        <div className="flex items-center gap-6">
          <div className="w-36 h-36 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={40} outerRadius={64} paddingAngle={2}>
                  {data.map(d => <Cell key={d.name} fill={d.color} stroke="none" />)}
                </Pie>
                <Tooltip content={<SplitTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-3">
            {data.map((d, i) => {
              const share = total > 0 ? (d.value / total) * 100 : 0
              const margin = i === 0 ? intragroupMarginPct : externalMarginPct
              return (
                <div key={d.name}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-white font-medium">{d.name}</span>
                    </span>
                    <span className="text-gray-300 text-sm">{formatCurrency(d.value)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-0.5 pl-[18px]">
                    <span>{share.toFixed(1)}% del revenue</span>
                    <span>Margen: {formatPct(margin)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
